import { LayoutDashboard, LogOut, Menu, Receipt, UserCog, X, type LucideIcon } from 'lucide-react'
import { useState, type ReactElement } from 'react'
import { NavLink, Outlet } from 'react-router-dom'

import {
  APP_HEADER_CLASS,
  SIDEBAR_ASIDE_CLASS,
  SIDEBAR_FOOTER_CLASS,
  SidebarBrand,
  sidebarNavItemClass,
} from '@/components/layout/sidebar'
import { ThemeToggle } from '@/components/shared/theme-toggle'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { AccountPaywallBanner } from '@/features/customer-portal/components/account-paywall-banner'
import { useAccountSignOut } from '@/features/customer-portal/hooks/use-account-sign-out'
import { useAuth } from '@/providers/auth-context'

interface AccountNavItem {
  to: string
  label: string
  icon: LucideIcon
}

const ACCOUNT_NAV_ITEMS: readonly AccountNavItem[] = [
  { to: '/conta', label: 'Visão geral', icon: LayoutDashboard },
  { to: '/conta/faturas', label: 'Faturas', icon: Receipt },
  { to: '/conta/perfil', label: 'Perfil', icon: UserCog },
]

export function AccountSidebarNav({ onNavigate }: { onNavigate?: () => void }): ReactElement {
  return (
    <nav className="flex-1 space-y-1 px-3">
      {ACCOUNT_NAV_ITEMS.map((item) => {
        const Icon = item.icon
        return (
          <NavLink
            key={item.to}
            to={item.to}
            end
            onClick={onNavigate}
            className={({ isActive }) => sidebarNavItemClass(isActive)}
          >
            <Icon className="h-4 w-4 shrink-0" aria-hidden />
            {item.label}
          </NavLink>
        )
      })}
    </nav>
  )
}

export function AccountSidebar(): ReactElement {
  return (
    <aside className={SIDEBAR_ASIDE_CLASS}>
      <SidebarBrand />
      <AccountSidebarNav />
      <div className={SIDEBAR_FOOTER_CLASS}>
        Portal do cliente
      </div>
    </aside>
  )
}

export function AccountMobileNav(): ReactElement {
  const [open, setOpen] = useState(false)
  const close = (): void => {
    setOpen(false)
  }

  return (
    <>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="md:hidden"
        aria-label="Abrir menu de navegação"
        onClick={() => {
          setOpen(true)
        }}
      >
        <Menu />
      </Button>

      {open && (
        <div className="fixed inset-0 z-50 md:hidden">
          <button
            type="button"
            aria-label="Fechar menu de navegação"
            className="absolute inset-0 cursor-default bg-black/50"
            onClick={close}
          />
          <aside className="absolute left-0 top-0 flex h-full w-64 flex-col bg-sidebar text-sidebar-foreground shadow-xl">
            <div className="flex items-center justify-between pr-3">
              <SidebarBrand />
              <Button type="button" variant="ghost" size="icon" aria-label="Fechar" onClick={close}>
                <X />
              </Button>
            </div>
            <AccountSidebarNav onNavigate={close} />
          </aside>
        </div>
      )}
    </>
  )
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  const [first, ...rest] = parts
  if (!first) return '?'
  const last = rest.at(-1)
  if (!last) return first.slice(0, 2).toUpperCase()
  return (first.charAt(0) + last.charAt(0)).toUpperCase()
}

export function AccountUserMenu(): ReactElement | null {
  const { user } = useAuth()
  const signOut = useAccountSignOut()
  const [open, setOpen] = useState(false)

  if (!user) return null

  return (
    <div className="relative">
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Menu do usuário"
        onClick={() => {
          setOpen((v) => !v)
        }}
        className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-accent hover:text-accent-foreground"
      >
        <Avatar className="h-8 w-8">
          <AvatarFallback>{initials(user.name)}</AvatarFallback>
        </Avatar>
        <span className="hidden font-medium sm:block">{user.name}</span>
      </button>

      {open && (
        <>
          <button
            type="button"
            aria-label="Fechar menu do usuário"
            className="fixed inset-0 z-40 cursor-default"
            onClick={() => {
              setOpen(false)
            }}
          />
          <div
            role="menu"
            className="absolute right-0 z-50 mt-2 w-56 rounded-md border border-border bg-popover p-1 text-popover-foreground shadow-md"
          >
            <div className="px-2 py-1.5">
              <p className="text-sm font-medium">{user.name}</p>
              <p className="truncate text-xs text-muted-foreground">{user.email}</p>
            </div>
            <div className="my-1 h-px bg-border" />
            <button
              type="button"
              role="menuitem"
              disabled={signOut.isPending}
              onClick={() => {
                signOut.mutate()
              }}
              className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent hover:text-accent-foreground disabled:opacity-50"
            >
              <LogOut className="h-4 w-4" aria-hidden />
              Sair
            </button>
          </div>
        </>
      )}
    </div>
  )
}

export function AccountHeader(): ReactElement {
  return (
    <header className={APP_HEADER_CLASS}>
      <AccountMobileNav />
      <div className="ml-auto flex items-center gap-1">
        <ThemeToggle />
        <AccountUserMenu />
      </div>
    </header>
  )
}

export function AccountShell(): ReactElement {
  return (
    <div className="flex min-h-screen bg-background">
      <AccountSidebar />
      <div className="flex min-w-0 flex-1 flex-col">
        <AccountHeader />
        <main className="flex-1 overflow-auto">
          <div className="mx-auto w-full max-w-screen-2xl p-4 sm:p-6">
            {/* Acima do conteúdo, em todas as telas do portal: o aviso acompanha o cliente sem
                impedir que ele navegue até as faturas ou a assinatura para resolver. */}
            <AccountPaywallBanner />
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  )
}
